import mime from 'mime-types';

/**
 * Capitalize a string by putting the first letter in uppercase and the rest in lowercase
 */
export const capitalize = (str: string): string => str.substring(0, 1).toUpperCase() + str.substring(1).toLowerCase();

export const capitalizeAll = (str: string): string => str.replace(/(^\w|\(\w)/g, word => word.toLocaleUpperCase());

/**
 * This code should NOT be used anymore, here for compatibility reasons with old `thunk` code.
 * Try n time to execute requestCallback with 1 second delay between tries.
 */
export const fetchRetry = async <T>(reject: (reason: string) => T, requestCallBack: () => Promise<T>, maxRetry = 5, timeout = 500): Promise<T> => {
  do {
    try {
      // This is expected to have an await inside a loop here
      // because we want to wait for the request to be done be retrying another one
      // `return await` allows us to catch the error and retry
      // eslint-disable-next-line no-await-in-loop
      return await requestCallBack();
    } catch {
      // eslint-disable-next-line no-param-reassign
      maxRetry--;
      // Wait for `timeout` milliseconds before retrying
      // eslint-disable-next-line no-await-in-loop, no-promise-executor-return
      await new Promise(resolve => setTimeout(resolve, timeout));
      // eslint-disable-next-line no-param-reassign
      timeout *= 2;
    }
  } while (maxRetry > 1);

  // All retries failed, reject the promise
  return reject('fetchRetry exhausted all `maxRetry` tries');
};

/**
 * Replaces every dot in a filename with an underscore, except the extension
 * separator (the last dot).
 */
export const sanitizeFileName = (fileName?: string): string | undefined => fileName?.replace(/\.(?=.*\.)/g, '_');

type DownloadOptions = {
  fileName?: string;
  /** mime type */
  type?: string;
};

/** Legacy IE-only API, absent from the standard `Navigator` type. */
type LegacyNavigator = Navigator & {
  msSaveOrOpenBlob?: (blob: Blob, defaultName?: string) => boolean;
};

/**
 * Downloads a base64 encoded file.
 * If `type` is provided, it will be used as the mime type.
 * If not, it will be guessed from the file name or leaved as is if already provided.
 */
export const downloadFile = (file: string, { fileName, type }: DownloadOptions = {}): void => {
  if (typeof file !== 'string') {
    throw new Error('downloadFile: file must be a string');
  }

  let dataUrl = file;
  if (typeof type === 'string') {
    dataUrl = `data:${type};base64,${file.replace(/^data:.*;base64,/i, '')}`;
  } else if (fileName && !file.startsWith('data:')) {
    const mimeType = mime.lookup(file);
    if (mimeType) {
      dataUrl = `data:${mimeType};base64,${file}`;
    }
  }

  // Internet Explorer 🙄
  const legacyNavigator = window.navigator as LegacyNavigator;
  if (legacyNavigator?.msSaveOrOpenBlob) {
    legacyNavigator.msSaveOrOpenBlob(convertBase64ToBlob(dataUrl), sanitizeFileName(fileName));
  } else {
    downloadURL(dataUrl, fileName);
  }
};

/**
 * Convert base64 to Blob.
 * @param base64 Base64 to convert to Blob
 * @param contentType Optional content type (will be overridden if provided in the b64)
 */
export const convertBase64ToBlob = (base64: string, contentType?: string): Blob => {
  let type = contentType;
  let decodedData = window.atob(base64);

  if (base64.startsWith('data:')) {
    [type, decodedData] = base64.split(';base64,');

    [, type] = type.split(':');
    decodedData = window.atob(decodedData);
  }

  const uInt8Array = new Uint8Array(decodedData.length);
  for (let i = 0; i < decodedData.length; i++) {
    uInt8Array[i] = decodedData.charCodeAt(i);
  }

  return new Blob([uInt8Array], { type });
};

/**
 * Downloads a file from its URL.
 * ⚠ Be aware that if the server return the header `Content-Disposition` with a filename, it will be used by the browser instead of the one provided.
 */
export const downloadURL = (url?: string, fileName?: string): void => {
  const sanitizedFileName = sanitizeFileName(fileName);

  const downloadLink = document.createElement('a');

  downloadLink.style.display = 'none';
  downloadLink.setAttribute('href', url ?? '');
  downloadLink.setAttribute('download', sanitizedFileName ?? '');

  document.body.appendChild(downloadLink);
  downloadLink.click();

  document.body.removeChild(downloadLink);
};

/**
 * Returns true if the input value is null or undefined or if the given value is its type's empty value; false otherwise.
 */
export const isNilOrEmpty = (value: unknown): boolean =>
  value === null || value === undefined || value === '' || Object.keys(value as object).length === 0;
